import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHotelAdminStore } from '../store'
import { ROUTES } from '../constants/routes'
import { apiClient } from '../services/apiClient'
import AdminInvitationCodeSection from '../components/AdminInvitationCodeSection'

interface Document {
  document_id: string
  user_id: number
  user_name: string
  user_email: string
  document_type: string
  status: string
  created_at: string
  file_url: string
  extraction_confidence: number
  extracted_data: Record<string, any>
  hotel_admin_viewed_at?: string
}

interface Stats {
  total_documents: number
  total_guests: number
  documents_this_month: number
  active_subscription: boolean
  subscription_expires_at: string
  unread_notifications: number
  referral_count: number
}

interface Notification {
  id: number
  type: string
  message: string
  is_read: boolean
  created_at: string
}

export default function HotelAdminDashboardPage() {
  const navigate = useNavigate()
  const { admin, logout, getAccessToken } = useHotelAdminStore()
  const isMounted = useRef(true)

  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'payments' | 'referrals' | 'templates' | 'settings'>(
    'overview'
  )
  const [documents, setDocuments] = useState<Document[]>([])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Template upload state
  const [templateFile, setTemplateFile] = useState<File | null>(null)
  const [templateType, setTemplateType] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false)
  const [adminTemplates, setAdminTemplates] = useState<any[]>([])
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [registrationUrl, setRegistrationUrl] = useState<string | null>(null)

  // Load data from API
  const loadAdminData = useCallback(async () => {
    try {
      if (!isMounted.current) return
      
      setIsLoading(true)
      setError(null)

      // Load stats
      const statsRes = await apiClient.getAdminStats('')
      if (isMounted.current) setStats(statsRes)

      // Load documents
      const docsRes = await apiClient.getAdminDocuments(1, 20)
      console.log('Documents response:', docsRes)
      console.log('🔴 Documents array:', docsRes.documents)
      console.log('🔴 First document hotel_admin_viewed_at:', docsRes.documents?.[0]?.hotel_admin_viewed_at)
      console.log('🔴 Pending count from API:', docsRes.pending_count)
      if (isMounted.current) {
        setDocuments(docsRes.documents || [])
        setPendingCount(docsRes.pending_count || 0)
      }

      // Load notifications
      const notifRes = await apiClient.getAdminNotifications(false, 1, 20)
      console.log('Notifications response:', notifRes)
      if (isMounted.current) setNotifications(notifRes.notifications || [])

      // Load templates
      try {
        const templatesRes = await apiClient.get('/admin/templates')
        if (isMounted.current) setAdminTemplates(templatesRes.templates || [])
      } catch (err) {
        console.error('Failed to load templates:', err)
      }
    } catch (err: any) {
      if (isMounted.current) {
        console.error('Failed to load admin data:', err)
        setError(err.response?.data?.message || 'Failed to load dashboard data')
      }
    } finally {
      if (isMounted.current) setIsLoading(false)
    }
  }, [apiClient])

  useEffect(() => {
    // Don't run if admin is null (after logout)
    if (!admin) {
      return
    }

    isMounted.current = true
    const abortController = new AbortController()

    // Only load data if admin is available
    if (admin) {
      loadAdminData()
    }

    return () => {
      isMounted.current = false
      abortController.abort()
    }
  }, [admin?.id, navigate, loadAdminData])

  const handleLogout = () => {
    logout()
    navigate(ROUTES.LOGIN)
  }

  const handleTemplateUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!templateFile) {
      alert('Выберите файл')
      return
    }

    setIsUploadingTemplate(true)
    
    try {
      const formData = new FormData()
      formData.append('file', templateFile)
      formData.append('template_type', templateType)
      formData.append('description', templateDescription)

      const response = await apiClient.post('/admin/templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      // Reload templates
      const templatesRes = await apiClient.get('/admin/templates')
      setAdminTemplates(templatesRes.templates || [])

      // Reset form
      setTemplateFile(null)
      setTemplateType('')
      setTemplateDescription('')

      window.alert('✅ Шаблон успешно отправлен владельцу на рассмотрение')
    } catch (err: any) {
      console.error('Template upload error:', err)
      window.alert('❌ Ошибка при загрузке шаблона')
    } finally {
      setIsUploadingTemplate(false)
    }
  }

  const handleMarkViewed = async (docId: string) => {
    try {
      console.log('🔴 Marking document as viewed:', docId)
      await apiClient.markDocumentViewed(docId)
      console.log('🔴 API call successful, updating local state')
      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.document_id === docId
            ? {
                ...doc,
                hotel_admin_viewed_at: new Date().toISOString(),
              }
            : doc
        )
      )
      // Update pending count
      setPendingCount((prev) => Math.max(0, prev - 1))
      console.log('🔴 Local state updated')
    } catch (err) {
      console.error('Failed to mark document as viewed:', err)
    }
  }

  const handleDownloadDocument = async (doc: Document) => {
    try {
      console.log('Downloading document:', doc.document_id, 'URL:', doc.file_url)
      
      // Fetch the document from the file_url
      const response = await fetch(doc.file_url)
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`)
      }
      
      // Create blob and trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `document_${doc.document_id.substring(0, 8)}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download document:', err)
      alert('Ошибка при скачивании документа')
    }
  }

  const handleDeleteDocument = async (docId: string) => {
    console.log('🔴 handleDeleteDocument called with docId:', docId)
    console.log('🔴 docId type:', typeof docId)
    console.log('🔴 docId value:', docId)

    if (!window.confirm('Вы уверены, что хотите удалить этот документ?')) {
      console.log('🔴 User cancelled deletion')
      return
    }

    try {
      console.log('🔴 Deleting document:', docId)
      await apiClient.deleteDocument(docId)
      console.log('🔴 Document deleted successfully')

      // Remove from local state
      setDocuments((prev) => prev.filter((doc) => doc.document_id !== docId))
      console.log('🔴 Local state updated')
    } catch (err) {
      console.error('Failed to delete document:', err)
      alert('Ошибка при удалении документа')
    }
  }

  const handleMarkNotificationRead = async (notifId: number) => {
    try {
      await apiClient.markNotificationRead(notifId)
      // Update local state
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notifId
            ? {
                ...notif,
                is_read: true,
              }
            : notif
        )
      )
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  }

  if (!admin) {
    return null
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Загрузка...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Header with logout button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Панель администратора</h1>
            <p className="text-xs sm:text-sm text-gray-600">{admin.hotel_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-3 sm:px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium transition text-sm sm:text-base touch-manipulation"
          >
            Выход
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-red-800">
              <span className="font-semibold">⚠️ Ошибка:</span> {error}
            </p>
          </div>
        )}

        {/* Subscription Status Alert */}
        {stats && (
          <>
            {stats.active_subscription ? (
              <div className="mb-4 sm:mb-6 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-green-800">
                  <span className="font-semibold">✅ Подписка активна</span> до{' '}
                  {new Date(stats.subscription_expires_at).toLocaleDateString('ru-RU')}
                </p>
              </div>
            ) : (
              <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-red-800">
                  <span className="font-semibold">⚠️ Подписка истекла</span> - пожалуйста, продлите подписку
                </p>
                <button 
                  onClick={() => navigate('/admin-subscription-select')}
                  className="text-sm text-red-600 hover:text-red-700 font-medium mt-2 underline"
                >
                  Оплатить подписку →
                </button>
              </div>
            )}
          </>
        )}

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-4 border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
          {[
            { id: 'overview', label: 'Обзор', icon: '📊' },
            {
              id: 'documents',
              label: `Документы (${pendingCount})`,
              icon: '📄',
            },
            { id: 'payments', label: 'Платежи', icon: '💳' },
            { id: 'referrals', label: 'Рефералы', icon: '🔗' },
            { id: 'templates', label: 'Шаблоны', icon: '📝' },
            { id: 'settings', label: 'Настройки', icon: '⚙️' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`pb-2 sm:pb-3 px-2 sm:px-4 font-medium transition flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm ${
                activeTab === tab.id
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="text-sm sm:text-base">{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.icon === '📄' ? 'Документы' : tab.label}</span>
              {tab.badge && (
                <span className="bg-red-500 text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[
                {
                  label: 'Всего документов',
                  value: stats.total_documents,
                  icon: '📊',
                  color: 'indigo',
                },
                {
                  label: 'Сгенерировано документов',
                  value: stats.generated_documents,
                  icon: '📄',
                  color: 'green',
                },
                {
                  label: 'Срок подписки',
                  value: new Date(stats.subscription_expires_at).toLocaleDateString('ru-RU'),
                  icon: '📅',
                  color: 'blue',
                },
                { label: 'Количество рефералов', value: stats.referral_count, icon: '👥', color: 'purple' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`bg-${stat.color}-50 border border-${stat.color}-200 rounded-lg p-6`}
                >
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Документы гостей ({documents.length})</h2>
            {documents.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-600">Документов не найдено</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.document_id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{doc.user_name || 'Гость'}</h3>
                        <p className="text-sm text-gray-600">{doc.document_type || 'Документ'}</p>
                        <p className="text-xs text-gray-600">Email: {doc.user_email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Создан: {new Date(doc.created_at).toLocaleString('ru-RU')}
                        </p>
                        <p className="text-xs text-gray-500">
                          Уверенность: {(doc.extraction_confidence * 100).toFixed(1)}%
                        </p>
                        {doc.hotel_admin_viewed_at && (
                          <p className="text-xs text-green-600 mt-1">
                            ✅ Просмотрено:{' '}
                            {new Date(doc.hotel_admin_viewed_at).toLocaleString('ru-RU')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            doc.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : doc.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {doc.hotel_admin_viewed_at
                            ? '✅ Просмотрено'
                            : doc.status === 'pending'
                            ? '⏳ В ожидании'
                            : doc.status === 'approved'
                            ? '✅ Одобрено'
                            : '❌ Отклонено'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleDownloadDocument(doc)}
                        className="px-3 py-2 sm:px-3 sm:py-2 bg-green-600 text-white rounded hover:bg-green-700 text-xs sm:text-sm font-medium transition flex items-center gap-1 sm:gap-2 touch-manipulation"
                      >
                        <span className="text-sm sm:text-base">📥</span>
                        <span className="hidden sm:inline">Скачать документ</span>
                        <span className="sm:hidden">Скачать</span>
                      </button>
                      {!doc.hotel_admin_viewed_at && (
                        <button
                          onClick={() => handleMarkViewed(doc.document_id)}
                          className="px-3 py-2 sm:px-3 sm:py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs sm:text-sm font-medium transition touch-manipulation"
                        >
                          ✓ Отметить
                        </button>
                      )}
                      {doc.hotel_admin_viewed_at && (
                        <span className="px-3 py-2 sm:px-3 sm:py-2 bg-green-100 text-green-800 rounded text-xs sm:text-sm font-medium">
                          ✓ Просмотрено
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          console.log('🔴 Delete button clicked, doc_id:', doc.document_id)
                          e.stopPropagation()
                          handleDeleteDocument(doc.document_id)
                        }}
                        className="px-3 py-2 sm:px-3 sm:py-2 bg-red-600 text-white rounded hover:bg-red-700 text-xs sm:text-sm font-medium transition touch-manipulation"
                      >
                        🗑️ Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            <h2 className="text-xl font-bold mb-4">История платежей и подписка</h2>
            
            {/* Subscription Info Card */}
            <div className={`rounded-lg border p-6 mb-6 ${
              stats?.active_subscription 
                ? 'bg-green-50 border-green-200' 
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-center gap-4">
                <div className="text-4xl">
                  {stats?.active_subscription ? '✅' : '⏱️'}
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-bold ${
                    stats?.active_subscription 
                      ? 'text-green-900' 
                      : 'text-orange-900'
                  }`}>
                    {stats?.active_subscription 
                      ? 'Подписка активна' 
                      : 'Подписка не активна'}
                  </h3>
                  <p className={`text-sm ${
                    stats?.active_subscription 
                      ? 'text-green-700' 
                      : 'text-orange-700'
                  }`}>
                    {stats?.active_subscription
                      ? `Действует до ${new Date(stats.subscription_expires_at).toLocaleDateString('ru-RU')}`
                      : 'Пожалуйста, оплатите подписку для доступа к сервису'}
                  </p>
                </div>
                <button 
                  onClick={() => navigate('/admin-subscription-select')}
                  className={`px-6 py-3 font-medium rounded-lg transition ${
                    stats?.active_subscription 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  {stats?.active_subscription ? '🔄 Продлить' : '💳 Оплатить'}
                </button>
              </div>
            </div>

            {/* Payment History */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Ваши платежи</h3>
              <div className="text-center py-8 text-gray-500">
                <p className="mb-2">💳 История платежей пуста</p>
                <p className="text-sm">Ваши платежи будут отображаться здесь</p>
              </div>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Рефералы</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-blue-800 mb-4">
                Система рефералов администраторов находится в разработке
              </p>
              <p className="text-sm text-blue-600">
                Скоро вы сможете делиться уникальным кодом с другими администраторами отелей
              </p>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <h2 className="text-xl font-bold mb-4">📝 Шаблоны документов</h2>
            <p className="text-gray-600 mb-6">
              Загрузите ваши шаблоны договоров для отправки владельцу на одобрение
            </p>
            
            {/* Upload Form */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Загрузить новый шаблон</h3>
              <form onSubmit={handleTemplateUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Файл шаблона
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тип шаблона
                  </label>
                  <select
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите тип</option>
                    <option value="contract">Договор</option>
                    <option value="agreement">Соглашение</option>
                    <option value="form">Форма</option>
                    <option value="other">Другое</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Описание
                  </label>
                  <textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Краткое описание шаблона..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={isUploadingTemplate}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {isUploadingTemplate ? '⏳ Загрузка...' : '📤 Отправить владельцу'}
                </button>
              </form>
            </div>

            {/* Templates List */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Ваши шаблоны</h3>
              {adminTemplates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-2">📝 Вы еще не загрузили шаблоны</p>
                  <p className="text-sm">Загрузите первый шаблон выше</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {adminTemplates.map((template) => (
                    <div key={template.template_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{template.file_name}</p>
                        <p className="text-sm text-gray-500">
                          {template.template_type} • {new Date(template.created_at).toLocaleDateString('ru-RU')}
                        </p>
                        {template.is_approved && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                            ✅ Одобрен
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {template.is_approved ? 'Одобрен' : 'На рассмотрении'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <AdminInvitationCodeSection />
          </div>
        )}
      </main>
    </div>
  )
}
